import { AfterViewInit, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { EventDetails, Media } from '../../shared/models/eventDetails';
import { Subject, catchError, takeUntil, tap } from 'rxjs';
import { VIDEO, FILE_DOWNLOAD_ERROR } from '../../shared/constants/camera-profile-constant';
import { MessageService, SlbSeverity } from '@slb-dls/angular-material/notification';
import { TimeZoneService } from '../../shared/services/time-zone.service';
import { DashboardService } from '../../shared/services/dashboard.service';
import { CameraProfileService } from '../../shared/services/camera-profile.service';
import { Bookmark } from '../../../../src/app/shared/models/global-view';

@Component({
  selector: 'app-global-event-grid',
  templateUrl: './global-event-grid.component.html',
  styleUrls: ['./global-event-grid.component.scss'],
})
export class GlobalEventGridComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() gridData: EventDetails[];
  @Input() totalNoRecords: number;
  @Input() paginatedData: PageEvent;
  @Input() eventId: number;
  @Input() isLoading: boolean;

  @Output() cameraPopOverClosed: EventEmitter<void> = new EventEmitter<void>();
  @Output() pageData: EventEmitter<PageEvent> = new EventEmitter<PageEvent>();
  @Output() selectedData: EventEmitter<EventDetails[]> = new EventEmitter<EventDetails[]>();
  @Output() eventBookmarked: EventEmitter<Bookmark> = new EventEmitter<Bookmark>();

  @ViewChild(MatPaginator) paginator: MatPaginator;

  public popOverCameraDetails: EventDetails | null;
  public currentZone = '';
  public hasNext = false;
  public hasPrevious = false;
  public selectedPopOverIndex = -1;
  public isOpen = false;
  public sessionToken: string;

  private initialBookmarkedState: boolean;
  private destroyed = new Subject();

  constructor(
    private timeZoneService: TimeZoneService,
    private messageService: MessageService,
    private dashboardService: DashboardService,
    private cameraProfileservice: CameraProfileService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.pageData
      .pipe(
        tap(() => {
          setTimeout(() => {
            document.getElementsByClassName('event-container')[0].scrollIntoView({
              behavior: 'smooth',
              block: 'end',
              inline: 'nearest',
            });
          }, 100);
        })
      )
      .subscribe();
    this.currentZone = this.timeZoneService.getTimeZone();
    this.sessionToken = sessionStorage.getItem('access_token') ?? '';
    if (changes && changes['gridData'] && changes['gridData'].currentValue !== '') {
      this.gridData?.map((data: EventDetails) => {
        if (data) {
          if (data?.media?.length > 0) {
            data.isVideo =
              data.media.filter((med: Media) => med.type.toLowerCase() === VIDEO && med.fileUrl !== '' && med.fileUrl !== null)?.length > 0;
          }
        }
      });
      this.popOverCameraDetails = this.selectedPopOverIndex !== -1 ? this.gridData[this.selectedPopOverIndex] : null;
    }

    if (this.paginator && this.paginatedData) {
      this.paginator.pageIndex = this.paginatedData.pageIndex;
      this.paginator.pageSize = this.paginatedData.pageSize;
    }
  }

  ngAfterViewInit(): void {
    if (this.paginator && this.paginatedData) {
      this.paginator.pageIndex = this.paginatedData?.pageIndex;
      this.paginator.pageSize = this.paginatedData?.pageSize;
    }
  }

  public downloadImage(cameraDetails: EventDetails): void {
    const urls = cameraDetails?.media.filter((url: Media) => url.fileUrl);
    if (urls.length > 0 && cameraDetails.gatewayId && cameraDetails.equipmentId) {
      this.cameraProfileservice
        .downloadMediaAsZip(cameraDetails.gatewayId, cameraDetails.equipmentId, [cameraDetails.eventId])
        .pipe(
          tap((data: string) => {
            if (data && data !== '') {
              this.dashboardService.downloadZipFile(data, cameraDetails.equipmentId);
            } else {
              this.messageService.add({ severity: SlbSeverity.Error, summary: FILE_DOWNLOAD_ERROR });
            }
          }),
          catchError(err => {
            this.messageService.add({ severity: SlbSeverity.Error, summary: FILE_DOWNLOAD_ERROR });
            throw err;
          }),
          takeUntil(this.destroyed)
        )
        .subscribe();
    } else {
      this.messageService.add({ severity: SlbSeverity.Error, summary: FILE_DOWNLOAD_ERROR });
    }
  }

  public checkBoxSelection(): void {
    const filteredFiles: EventDetails[] = this.gridData.filter((data: EventDetails) => data && data.isChecked);
    this.selectedData.emit(filteredFiles);
  }

  public onImageClick(EventDetail: EventDetails, index: number): void {
    this.isOpen = !this.isOpen;
    this.selectedPopOverIndex = index;
    const mapData: EventDetails = EventDetail;
    mapData.equipmentId = EventDetail.equipmentId;
    mapData.equipmentName = EventDetail.equipmentName;
    const pageEvent = this.paginatedData
      ? this.paginatedData
      : ({
          pageIndex: 0,
          previousPageIndex: 0,
          pageSize: 10,
          length: this.totalNoRecords,
        } as PageEvent);
    this.checkNext();
    this.checkPrevious(pageEvent);
    this.popOverCameraDetails = mapData;
  }

  public onEventClick(event: number): void {
    const action = event === this.selectedPopOverIndex - 1 ? 'prev' : 'next';
    this.selectedPopOverIndex = event;
    const pageEvent = this.paginatedData
      ? this.paginatedData
      : ({
          pageIndex: 0,
          previousPageIndex: 0,
          pageSize: 10,
          length: this.totalNoRecords,
        } as PageEvent);
    this.checkNext();
    this.checkPrevious(pageEvent);
    if (this.gridData[this.selectedPopOverIndex]) {
      const data: EventDetails = this.gridData[this.selectedPopOverIndex];
      this.popOverCameraDetails = data;
    } else if (this.hasNext) {
      pageEvent.pageIndex = action === 'next' ? pageEvent.pageIndex + 1 : pageEvent.pageIndex - 1;
      this.selectedPopOverIndex = action === 'next' ? 0 : pageEvent.pageSize - 1;
      this.pageData.emit(pageEvent);
    }
  }

  public checkNext(): void {
    this.hasNext = this.gridData.length > this.selectedPopOverIndex + 1;
  }

  public checkPrevious(pageEvent: PageEvent): void {
    this.hasPrevious = this.selectedPopOverIndex <= 0 && pageEvent.pageIndex === 0 ? false : true;
  }

  public updateParentData(event: EventDetails): void {
    this.gridData[this.selectedPopOverIndex] = event;
  }

  public pageEvent(event: PageEvent): void {
    this.selectedData.emit([]);
    this.pageData.emit(event);
  }

  public closeCameraPopOver(): void {
    this.popOverCameraDetails = null;
    this.isOpen = false;
    this.cameraPopOverClosed.emit();
  }

  public bookmarkEvent(event: EventDetails): void {
    this.eventId = event.eventId;
    this.initialBookmarkedState = event.isBookmarked;
    event.isBookmarked = true;
    this.eventBookmarked.emit({ eventId: event.eventId });
  }

  public removeBookmarkedEvent(event: EventDetails): void {
    this.eventId = event.eventId;
    this.initialBookmarkedState = event.isBookmarked;
    event.isBookmarked = false;
    this.eventBookmarked.emit({ eventId: event.eventId });
  }

  public revertToInitialBookmarkedState(eventId: number): void {
    const event = this.gridData.filter(e => e.eventId === eventId)[0];
    event.isBookmarked = this.initialBookmarkedState;
  }

  ngOnDestroy(): void {
    this.destroyed.next(true);
  }
}
